import { useQuery } from "@apollo/client";
import {
  Card,
  CardContent,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useIsSuperAdmin } from "../../App";
import { PageContainer } from "../../components/PageContainer";
import { LoadingOverlay } from "../../components/Loading";
import BandsTable from "./table";
import EditEditionBandsCalendarView from "./calendarView";
import { GET_EDITION_FOR_EDIT } from "./graphql";
import {
  ScheduleDaySelector,
  DayOption,
} from "../../components/ScheduleDaySelector";
import type { FestivalEditionForEditQuery } from "../../__generated__/graphql";
import { parseBooleanEnvFlag } from "../../utils/envFlags";

type FestivalEditionForEdit = NonNullable<
  FestivalEditionForEditQuery["festivalEdition"]
>;
export type EditableBand = FestivalEditionForEdit["bands"][number] & {
  source?: string | null;
};

const ALL_DAYS_KEY = "__ALL_DAYS__";

interface EditEditionBandsContentProps {
  inline?: boolean;
}

export const EditEditionBandsContent = ({
  inline = false,
}: EditEditionBandsContentProps) => {
  const isSuperAdmin = useIsSuperAdmin();
  const { id } = useParams();
  const { data, loading, refetch } = useQuery(GET_EDITION_FOR_EDIT, {
    variables: {
      id: id!,
    },
    fetchPolicy: "cache-first",
  });
  const [selectedDate, setSelectedDate] = useState<string | undefined>();
  const [groupByAnnouncement, setGroupByAnnouncement] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");
  const canUseCalendar = parseBooleanEnvFlag(
    process.env.REACT_APP_ENABLE_CALENDAR,
    isSuperAdmin
  );

  const editionBands = useMemo<EditableBand[]>(
    () =>
      data?.festivalEdition?.bands.map((band) => ({
        ...band,
        source: "Edition",
      })) ?? [],
    [data]
  );

  const announcementBands = useMemo<EditableBand[]>(
    () =>
      data?.festivalEdition?.announcements.flatMap((announcement) =>
        announcement.bands
          .map((band) => ({
            ...band,
            source: announcement.name ?? "Announcement",
          }))
      ) ?? [],
    [data]
  );

  const announcementOptions = useMemo(
    () =>
      (data?.festivalEdition?.announcements ?? [])
        .map((announcement) => ({
          id: announcement.id,
          name: announcement.name?.trim() || "Announcement",
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );

  const announcementBandOptions = useMemo(() => {
    return (data?.festivalEdition?.announcements ?? [])
      .flatMap((announcement) =>
        announcement.bands
          .filter((booking) => {
            const hasSchedule = Boolean(
              booking.date ||
                booking.startTime ||
                booking.endTime ||
                booking.stage?.id
            );
            return !hasSchedule;
          })
          .map((booking) => ({
            bookingId: booking.id,
            announcementId: announcement.id,
            announcementName: announcement.name?.trim() || "Announcement",
            bandId: booking.band.id,
            bandName: booking.band.name?.trim() || "Unknown band",
          }))
      )
      .sort((a, b) => {
        const byBand = a.bandName.localeCompare(b.bandName);
        if (byBand !== 0) return byBand;
        return a.announcementName.localeCompare(b.announcementName);
      });
  }, [data]);

  const allBands = useMemo<EditableBand[]>(
    () => [...editionBands, ...announcementBands],
    [editionBands, announcementBands]
  );

  const bandsByDay = useMemo(() => {
    return allBands.reduce<Map<string, EditableBand[]>>((prev, next) => {
      const date = dayjs(next.startTime).utc(true);

      if (!date.isValid()) {
        const bands = prev.get("No Date") ?? [];
        prev.set("No Date", [...bands, next]);
        return prev;
      }

      let effectDate = date.format("YYYY/MM/DD");

      if (date.hour() < 5) {
        effectDate = date.subtract(1, "day").format("YYYY/MM/DD");
      }

      const bands = prev.get(effectDate) ?? [];
      prev.set(effectDate, [...bands, next]);
      return prev;
    }, new Map());
  }, [allBands]);

  const handleSelectDay = (dateKey: string) => {
    setSelectedDate(dateKey);
  };

  const festivalDayKeys = useMemo(() => {
    const keys = (data?.festivalEdition?.days ?? [])
      .map((item) => {
        const date = dayjs(item.date).utc(true);
        if (!date.isValid()) return null;
        return date.format("YYYY/MM/DD");
      })
      .filter((item): item is string => Boolean(item));

    return Array.from(new Set(keys)).sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  }, [data]);

  const sortedKeys = useMemo(() => {
    const keySet = new Set<string>(festivalDayKeys);
    Array.from(bandsByDay.keys()).forEach((key) => keySet.add(key));

    const keys = Array.from(keySet);
    return keys.sort((a, b) => {
      const aDate = dayjs(a);
      const bDate = dayjs(b);
      const aValue = aDate.isValid() ? aDate.valueOf() : Number.MAX_SAFE_INTEGER;
      const bValue = bDate.isValid() ? bDate.valueOf() : Number.MAX_SAFE_INTEGER;

      if (aValue === bValue) {
        return a.localeCompare(b);
      }

      return aValue - bValue;
    });
  }, [bandsByDay, festivalDayKeys]);

  const firstValidDayKey = useMemo(
    () => sortedKeys.find((item) => dayjs(item).isValid()),
    [sortedKeys]
  );

  useEffect(() => {
    if (sortedKeys.length === 0) {
      setSelectedDate(undefined);
      return;
    }

    if (!selectedDate) {
      setSelectedDate(sortedKeys[0]);
      return;
    }

    if (
      selectedDate !== ALL_DAYS_KEY &&
      !sortedKeys.includes(selectedDate)
    ) {
      setSelectedDate(sortedKeys[0]);
    }
  }, [sortedKeys, selectedDate]);

  useEffect(() => {
    if (viewMode !== "calendar") return;
    if (selectedDate !== ALL_DAYS_KEY) return;
    if (!firstValidDayKey) return;

    setSelectedDate(firstValidDayKey);
  }, [firstValidDayKey, selectedDate, viewMode]);

  useEffect(() => {
    if (canUseCalendar) return;
    if (viewMode === "table") return;
    setViewMode("table");
  }, [canUseCalendar, viewMode]);

  const dayOptions: DayOption[] = useMemo(() => {
    if (sortedKeys.length === 0) return [];

    const options: DayOption[] = [];
    const validDayCount = sortedKeys.filter((item) => dayjs(item).isValid()).length;

    options.push({
      key: ALL_DAYS_KEY,
      title: "All Days",
      subtitle: `${validDayCount} day${validDayCount === 1 ? "" : "s"}`,
    });

    sortedKeys.forEach((item) => {
      const isValid = dayjs(item).isValid();
      options.push({
        key: item,
        title: isValid ? dayjs(item).format("dddd DD") : "No Date",
        subtitle: isValid ? dayjs(item).format("YYYY/MM/DD") : "-",
      });
    });

    return options;
  }, [sortedKeys]);

  const defaultDate = sortedKeys[0];
  const visibleDayOptions = useMemo(() => {
    if (viewMode === "calendar") {
      return dayOptions.filter(
        (item) => item.key !== ALL_DAYS_KEY && dayjs(item.key).isValid()
      );
    }

    return dayOptions;
  }, [dayOptions, viewMode]);

  const selectedDayKey = useMemo(() => {
    if (viewMode === "calendar") {
      if (
        selectedDate &&
        selectedDate !== ALL_DAYS_KEY &&
        dayjs(selectedDate).isValid()
      ) {
        return selectedDate;
      }

      return visibleDayOptions[0]?.key;
    }

    return selectedDate ?? defaultDate;
  }, [defaultDate, selectedDate, viewMode, visibleDayOptions]);

  const selectedBands = useMemo(() => {
    if (dayOptions.length === 0) return [];

    if (viewMode === "calendar") {
      if (!selectedDayKey) return [];
      return bandsByDay.get(selectedDayKey) ?? [];
    }

    if (selectedDate === ALL_DAYS_KEY) {
      return Array.from(bandsByDay.values()).flat();
    }

    const key = selectedDate ?? defaultDate;
    if (!key) return [];
    return bandsByDay.get(key) ?? [];
  }, [bandsByDay, dayOptions, defaultDate, selectedDate, selectedDayKey, viewMode]);

  const groupBy = groupByAnnouncement ? "announcement" : "stage";

  const sortedBands = useMemo(() => {
    if (selectedBands.length === 0) return [];

    const bands = [...selectedBands];

    bands.sort((a, b) => {
      const groupKeyA =
        groupBy === "announcement"
          ? (a.source && a.source.trim().length > 0 ? a.source : "Edition")
          : a.stage?.name ?? "No Stage";
      const groupKeyB =
        groupBy === "announcement"
          ? (b.source && b.source.trim().length > 0 ? b.source : "Edition")
          : b.stage?.name ?? "No Stage";

      if (groupKeyA !== groupKeyB) {
        return groupKeyA.localeCompare(groupKeyB);
      }

      return dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf();
    });

    return bands;
  }, [groupBy, selectedBands]);

  const renderContent = () => (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}
            >
              Select a day
            </Typography>
            {dayOptions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No bookings found for this edition.
              </Typography>
            ) : (
              <ScheduleDaySelector
                days={visibleDayOptions}
                selectedKey={
                  selectedDayKey ?? visibleDayOptions[0]?.key
                }
                onSelect={handleSelectDay}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            <Stack
              direction="row"
              spacing={1.5}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              {canUseCalendar && (
                <ToggleButtonGroup
                  color="primary"
                  exclusive
                  size="small"
                  value={viewMode}
                  onChange={(_, value: "table" | "calendar" | null) => {
                    if (!value) return;
                    setViewMode(value);
                  }}
                >
                  <ToggleButton value="table">Table</ToggleButton>
                  <ToggleButton value="calendar">Calendar</ToggleButton>
                </ToggleButtonGroup>
              )}
              {viewMode === "table" && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={groupByAnnouncement}
                      onChange={(_, checked) => setGroupByAnnouncement(checked)}
                    />
                  }
                  label="Group by announcement"
                />
              )}
            </Stack>
            {viewMode === "table" && sortedBands.length > 0 ? (
              <BandsTable
                key={`${selectedDate ?? defaultDate ?? "none"}-${groupBy}`}
                bands={sortedBands}
                groupBy={groupBy}
              />
            ) : viewMode === "table" && dayOptions.length > 0 ? (
              <Typography variant="body2" color="text.secondary">
                Select a day to bulk edit bookings.
              </Typography>
            ) : selectedDayKey && id ? (
              <EditEditionBandsCalendarView
                dayKey={selectedDayKey}
                bands={allBands}
                announcements={announcementOptions}
                announcementBands={announcementBandOptions}
                onRefresh={async () => {
                  await refetch();
                }}
              />
            ) : dayOptions.length > 0 ? (
              <Typography variant="body2" color="text.secondary">
                Select a day to use the calendar scheduler.
              </Typography>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );

  return (
    <LoadingOverlay loading={loading}>
      {inline ? (
        renderContent()
      ) : (
        <PageContainer
          title={`Bulk update: ${data?.festivalEdition?.name ?? ""}`}
        >
          {renderContent()}
        </PageContainer>
      )}
    </LoadingOverlay>
  );
};

const EditEditionBands = () => {
  return <EditEditionBandsContent />;
};

export default EditEditionBands;
